import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import _ from "lodash";

import "./searchFilter.css";

import CheckInput from "./checkInput";
import RangeSelector from "./RangeSelector";
import { roundNumberMax, roundNumberMin } from "../../../utils/math";
import { generateListingUrl } from "../../../utils/formatting/listing/url.utils";
import Button from "../../global/button";
import { generateDestinationListingUrl } from "../../../utils/formatting/distinations-listing/url.utils";

const FiltersDesktop = ({ filters = [], paramFilters = {}, isDestinations = false, summary = {}, params: queryParams = {}, isPriceNeeded = true }) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // I am returning the handle change funtion from the custom hook
  const handleCommitedChange = useHandleSliderChange(handleNavigation);

  function handleNavigation({ price, starRating, review, policy, propertyTypeIds, meals }) {
    if (isDestinations) {
      const searchParams = new URLSearchParams(location.search);
      const sortType = searchParams.get("sortType");

      const { destination } = params;

      let selectedPropertyType = null;

      if (propertyTypeIds) {
        if (propertyTypeIds.checked) {
          selectedPropertyType = filters[1]?.items.find((item) => item.value === propertyTypeIds.item);
        }
      } else {
        if (paramFilters.propertyTypeIds && paramFilters.propertyTypeIds?.filter((boolean) => boolean).length > 0) {
          selectedPropertyType = filters[1]?.items.find((item) => item.value === paramFilters.propertyTypeIds[0]);
        }
      }

      let selectedStarRatings = new Set(paramFilters.starRating || []);
      if (starRating && starRating.item !== undefined) {
        if (starRating.checked) {
          selectedStarRatings.add(starRating.item);
        } else {
          selectedStarRatings.delete(starRating.item);
        }
      }
      selectedStarRatings = Array.from(selectedStarRatings).map(Number);

      const selectFilters = {
        starRating: selectedStarRatings,
      };

      if (selectedPropertyType) {
        selectFilters.propertyType = `${selectedPropertyType.name}s`;
      }

      const url = generateDestinationListingUrl({
        searchCriteria: {
          location: {
            label: destination,
          },
        },
        filters: selectFilters,
        sortType,
      });

      return navigate(url);
    }

    price = price || paramFilters.price;
    review = review || paramFilters.score;

    let _policy = handleCheckItem(policy, "cancelationPolicyTypes", queryParams);

    let _propertyTypes = handleCheckItem(propertyTypeIds, "propertyTypeIds", queryParams);
    let _starRating = handleCheckItem(starRating, "starRating", queryParams);

    let _meals = handleCheckItem(meals, "meals", queryParams);

    // instead of this funtion make a hook which will return a function that will create the navigation url

    const selectedFilters = {
      minPrice: price[0],
      maxPrice: price[1],

      starRating: _starRating,

      minReviewScore: review[0],
      maxReviewScore: review[1],
      cancelationPolicyTypes: _policy,

      meals: _meals,

      propertyTypeIds: _propertyTypes,
    };

    const url = generateListingUrl({
      params: queryParams,
      filters: selectedFilters,
      sortType: queryParams.sortOptions?.type ?? null,
    });

    navigate(url);
  }

  function handleClear() {
    if (isDestinations) {
      return;
    }

    const url = generateListingUrl({ params: queryParams, filters: {} });

    navigate(url);
  }

  const minPrice = summary?.minPrice || 1;
  const maxPrice = summary?.maxPrice || 400;

  const selectedPriceRange = [paramFilters.price[0] || minPrice, paramFilters.price[1] || maxPrice];

  if (isDestinations) {
    isPriceNeeded = false;
  }
  const isFiltersEmpty = () => {
    return (
      (paramFilters.price.every((val) => val === null) || paramFilters.price.length === 0) &&
      (paramFilters.score.every((val) => val === null) || paramFilters.score.length === 0) &&
      paramFilters.policy.length === 0 &&
      paramFilters.propertyTypeIds.length === 0 &&
      paramFilters.starRating.length === 0 &&
      paramFilters.meals.length === 0
    );
  };

  return (
    <aside className="searchFilter">
      <div className="searchFilter__header">
        <h4>Filter by: </h4>
        {!isFiltersEmpty() && !isDestinations && (
          <Button onClick={handleClear} variant="secondary" className="reset-filters">
            Clear
          </Button>
        )}
      </div>

      <>
        {isPriceNeeded && minPrice !== maxPrice && (
          <RangeSelector
            initialvalue={selectedPriceRange}
            heading="Your budget"
            min={minPrice}
            max={maxPrice}
            step={calculateSmartStep(minPrice, maxPrice)}
            handleCommitedChange={handleCommitedChange}
            isPrice={true}
          />
        )}

        {/* <RangeSelector
            initialvalue={selectedReviewRange}
            heading="Review score"
            min={minReview}
            max={maxReview}
            start={`${minReview} score`}
            end={`${maxReview} score`}
            handleCommitedChange={handleCommitedChange}
          /> */}
      </>

      {filters.map((item) => {
        if (!item.items || item.items?.length === 0) return null;

        return (
          <CheckInput
            key={item.value}
            value={item.value}
            heading={item.title}
            conditions={item.items}
            handleNavigation={handleNavigation}
            paramFilters={paramFilters}
          />
        );
      })}
    </aside>
  );
};

export default FiltersDesktop;

function useHandleSliderChange(handleNavigation) {
  return (newValue, heading) => {
    if (newValue[0] === newValue[1]) {
      return;
    }

    if (heading === "Your budget") {
      let price = newValue;

      handleNavigation({ price });
    } else if (heading === "Review score") {
      let review = newValue;
      handleNavigation({ review });
    }
  };
}

function handleCheckItem(value = {}, heading = "", queryParams) {
  const { item, checked } = value;

  const data = new Set(queryParams?.filters[heading] || []);

  if (!value.item) return Array.from(data);

  if (checked) {
    data.add(item);
  } else {
    data.delete(item);
  }

  return Array.from(data);
}

function calculateSmartStep(minPrice, maxPrice, desiredSteps = 100) {
  const range = maxPrice - minPrice;

  // Initial rough step
  let roughStep = range / desiredSteps;

  // Round to a "nice" number (1, 2, 5, 10, 20, 50, 100, etc.)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;

  let niceStep;
  if (residual <= 1) {
    niceStep = 1 * magnitude;
  } else if (residual <= 2) {
    niceStep = 2 * magnitude;
  } else if (residual <= 5) {
    niceStep = 5 * magnitude;
  } else {
    niceStep = 10 * magnitude;
  }

  return niceStep;
}
